import { useRef, type ChangeEvent, type MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { ErrorMessage, type FieldInputProps } from 'formik';
import memoize from 'memoize-one';
import s from './FileUploadInput.module.scss';
import InfoBubble from '../../InfoBubble/InfoBubble';
import InputErrorMessage from '../shared/InputErrorMessage';
import xIcon from '../../../assets/icons/x.svg';
import { getFileFormatsMessageFromAllowedExtensions } from '../shared/fileUpload';
import type { FormikSetFieldValueForm } from '../shared/formikTypes';

const convertAllowedExtensions = memoize(
  (allowedExtensions: string[], intl: IntlShape) => {
    const accept = allowedExtensions.join(',');
    const fileFormats = getFileFormatsMessageFromAllowedExtensions(
      allowedExtensions,
      intl
    );
    return {
      accept,
      fileFormats,
    };
  }
);

interface FileUploadInputProps {
  field: FieldInputProps<File | null>;
  form: FormikSetFieldValueForm<File | null>;
  allowedExtensions?: string[];
  ariaDescribedBy?: string;
}

function FileUploadInput({
  field,
  form,
  allowedExtensions,
  ariaDescribedBy,
}: FileUploadInputProps) {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement | null>(null);

  let accept: string | undefined;
  let fileFormats: string | undefined;
  if (allowedExtensions) {
    ({ accept, fileFormats } = convertAllowedExtensions(
      allowedExtensions,
      intl
    ));
  }

  const { name, value } = field;

  const currentFileNameLabel = `${name}-currentFileNameLabel`;
  const addAFileLabel = `${name}-addAFileLabel`;
  const describedBy = [ariaDescribedBy, addAFileLabel]
    .filter(Boolean)
    .join(' ');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    form
      .setFieldValue(name, event.currentTarget.files?.[0] ?? null)
      .catch(() => undefined);
  };

  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    form.setFieldValue(name, null).catch(() => undefined);
  };

  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  /* oxlint-disable jsx-a11y/click-events-have-key-events */
  /* oxlint-disable jsx-a11y/no-static-element-interactions */
  /* oxlint-disable jsx-a11y/no-noninteractive-element-interactions */
  return (
    <div className={s.fileInputLabelWrapper}>
      <div className={s.wrapper}>
        {/* oxlint-disable-next-line jsx-a11y/label-has-associated-control */}
        <div id={addAFileLabel}>
          <FormattedMessage id="offerWizard.fileUpload.addFile" />
        </div>
        <div className={s.fileInputWrapper}>
          <input
            ref={inputRef}
            className={s.fileInput}
            id={name}
            name={name}
            type="file"
            aria-labelledby={addAFileLabel}
            aria-describedby={describedBy || undefined}
            onChange={handleChange}
            multiple={false}
            accept={accept}
          />
          <div className={s.fileInputWidget}>
            <div className={s.fileInputAreaAndButton}>
              <div className={s.file}>
                <div className={s.fileName}>
                  <div title={value?.name} id={currentFileNameLabel}>
                    {value?.name}
                  </div>
                </div>
                {!!value && (
                  <div>
                    <button
                      className={s.clearButton}
                      type="button"
                      onClick={handleClear}
                      aria-describedby={currentFileNameLabel}
                    >
                      <div className={s.clearIconWrapper}>
                        <img
                          src={xIcon}
                          alt={intl.formatMessage({
                            id: 'offerWizard.fileUpload.removeFile',
                          })}
                        />
                      </div>
                    </button>
                  </div>
                )}
              </div>
              <button
                className={s.chooseFileButton}
                type="button"
                onClick={handleButtonClick}
                aria-describedby={describedBy}
              >
                <div>
                  <FormattedMessage id="offerWizard.fileUpload.chooseFile" />
                </div>
              </button>
            </div>
            <div className={s.infoBubbleWrapper}>
              <InfoBubble>
                <FormattedMessage
                  id="offerWizard.fileUpload.info1"
                  values={{ fileFormats }}
                />
                <br />
                <FormattedMessage id="offerWizard.fileUpload.info2" />
              </InfoBubble>
            </div>
          </div>
        </div>
        <ErrorMessage name={name} component={InputErrorMessage} />
      </div>
    </div>
  );
}

export default FileUploadInput;
